import { useLayoutEffect, useState } from 'react';
import './edit-panel.css';
import './compact-panel.css';

/** Position a long mobile form in the visual viewport without scrolling the background page. */
export function useEditPanelViewport(open: boolean) {
  const [panel, panelRef] = useState<HTMLElement | null>(null);
  const [body, bodyRef] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!open || !panel || !body) return;
    const viewport = window.visualViewport;
    let frame = 0;
    const revealField = () => {
      const field = document.activeElement;
      if (
        !(field instanceof HTMLElement) ||
        !body.contains(field) ||
        !field.matches('input,textarea,select,[contenteditable="true"]')
      )
        return;
      const bounds = field.getBoundingClientRect();
      const area = body.getBoundingClientRect();
      // A textarea can be taller than the available keyboard viewport. Do not align
      // its entire box to the bottom and hide the first lines; leave caret scrolling native.
      if (bounds.height > area.height - 24) {
        if (bounds.top >= area.bottom - 24) body.scrollTop += bounds.top - area.top - 12;
        else if (bounds.bottom <= area.top + 24) body.scrollTop -= area.top + 12 - bounds.bottom;
        return;
      }
      if (bounds.bottom > area.bottom - 12) body.scrollTop += bounds.bottom - area.bottom + 12;
      else if (bounds.top < area.top + 12) body.scrollTop -= area.top + 12 - bounds.top;
    };
    const reset = () => {
      panel.style.removeProperty('--edit-panel-height');
      panel.style.removeProperty('--edit-panel-top');
      panel.style.removeProperty('--edit-panel-bottom');
      delete panel.dataset.panelMobile;
      delete panel.dataset.keyboardOpen;
    };
    const update = () => {
      cancelAnimationFrame(frame);
      panel.style.setProperty('--motion-exit-height', `${panel.offsetHeight}px`);
      if (!window.matchMedia('(max-width: 1023px)').matches) {
        reset();
        return;
      }
      panel.dataset.panelMobile = 'true';
      panel.style.setProperty('--edit-panel-height', `${viewport?.height ?? window.innerHeight}px`);
      panel.style.setProperty('--edit-panel-top', `${viewport?.offsetTop ?? 0}px`);
      panel.style.setProperty(
        '--edit-panel-bottom',
        `${Math.max(0, window.innerHeight - (viewport?.height ?? window.innerHeight) - (viewport?.offsetTop ?? 0))}px`,
      );
      panel.dataset.keyboardOpen = String(
        Boolean(viewport && window.innerHeight - viewport.height > 80),
      );
      frame = requestAnimationFrame(revealField);
    };
    update();
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    panel.addEventListener('focusin', update);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(body);
    return () => {
      cancelAnimationFrame(frame);
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      panel.removeEventListener('focusin', update);
      observer?.disconnect();
      // Retain geometry until Radix Presence finishes the close animation.
      // The detached node is discarded; a reopened node is measured afresh.
    };
  }, [open, panel, body]);
  return { panelRef, bodyRef };
}
